import React from 'react'
import {BuilderImage} from 'hec/types/common'
import {DialogContent, DialogHeader, DialogTitle, DialogDescription} from '@/components/ui/dialog'
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip'
import TooltipIcon from 'hec/assets/icons/tooltip'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
// Logo import removed; now passed as prop
import {useIntl} from 'react-intl'
import {formatISODateToMMDDYYYY} from '@/utils/common'

interface BalanceData {
    accountName: string
    balance: number
    expiryDate: string
    carryoverBalance?: number
}

interface BalanceModalProps {
    balancePopupText: string
    balancePopupData: BalanceData[]
    activeCurrency: string
    logo?: BuilderImage
}

const BalanceModal: React.FC<BalanceModalProps> = ({
    balancePopupText,
    balancePopupData,
    activeCurrency,
    logo
}) => {
    const intl = useIntl()

    return (
        <DialogContent
            className="max-w-[35.25rem] p-0 gap-0 translate-y-0 top-[1.75rem]"
            data-testid="balanceModal-DialogContent"
            closeIcon={
                <div className="p-4 -m-4 opacity-50 hover:opacity-75">
                    <span
                        className="[text-shadow:_0_1px_#fff] font-grotesque text-black text-[1.5rem]/[1rem] font-medium"
                        aria-hidden="true"
                    >
                        ×
                    </span>
                </div>
            }
        >
            <DialogHeader
                className="p-4 border-b border-b-[#dee2e6]"
                data-testid="balanceModal-DialogHeader"
            >
                <DialogTitle data-testid="balanceModal-DialogTitle">
                    {logo?.src && (
                        <img src={logo.src} alt={logo?.altText || ''} className="w-[12.5rem]" />
                    )}
                </DialogTitle>
                <VisuallyHidden.Root>
                    <DialogDescription data-testid="balanceModal-DialogDescription">
                        Balance Display content
                    </DialogDescription>
                </VisuallyHidden.Root>
            </DialogHeader>
            <div className="p-4">
                <p className="mb-4 font-grotesque text-sm leading-normal  text-emphasis">
                    {balancePopupText}
                </p>
                <table className="table-fixed w-full font-grotesque font-medium text-xs text-foreground border-separate border-spacing-x-0 border-spacing-y-4">
                    <tbody>
                        <tr className="bg-gray-01">
                            <th className="p-2.5 pr-0 text-left">Account</th>
                            <th className="py-2.5 px-0 text-left">Left to Spend</th>
                            <th className="p-2.5 pl-0 text-left">Deadline Date</th>
                            <th className="py-2.5 px-0 text-left flex items-center">
                                Eligible Carryover
                                <TooltipProvider data-testid="balanceModal-TooltipProvider">
                                    <Tooltip data-testid="balanceModal-Tooltip" defaultOpen={false}>
                                        <TooltipTrigger
                                            data-testid="balanceModal-TooltipTrigger"
                                            className="ml-1"
                                        >
                                            <TooltipIcon />
                                        </TooltipTrigger>
                                        <TooltipContent
                                            data-testid="balanceModal-TooltipContent"
                                            side="bottom"
                                        >
                                            <p className="font-normal">
                                                This is the amount you are eligible to carry over
                                                from your current plan year to the next.
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </th>
                        </tr>
                        {balancePopupData?.map(
                            ({accountName, balance, expiryDate, carryoverBalance}) => {
                                const balanceAmount = intl.formatNumber(balance, {
                                    style: 'currency',
                                    currency: activeCurrency
                                })

                                return (
                                    <tr key={`${accountName}-${expiryDate}`}>
                                        <td className="leading-normal">{accountName}</td>
                                        <td className="leading-normal">{balanceAmount}</td>
                                        <td className="leading-normal">
                                            {formatISODateToMMDDYYYY(expiryDate)}
                                        </td>

                                        <td className="leading-normal">
                                            {carryoverBalance
                                                ? intl.formatNumber(carryoverBalance, {
                                                      style: 'currency',
                                                      currency: activeCurrency
                                                  })
                                                : '-'}
                                        </td>
                                    </tr>
                                )
                            }
                        )}
                    </tbody>
                </table>
            </div>
        </DialogContent>
    )
}

export default BalanceModal
